import { apiRequest, ApiError, resolveApiUrl } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

/**
 * Fetch the current authenticated user.
 * GET /api/users/me → { user: { ..., internalChatEligible }, ... }
 *
 * Used to confirm live eligibility (e.g. whether to show the Chats menu) independently of the
 * possibly-stale login payload.
 */
export async function fetchCurrentUser({ token } = {}) {
  const payload = await apiRequest("/api/users/me", {
    method: "GET",
    headers: createAuthHeaders(token),
  });

  return {
    user: payload?.user && typeof payload.user === "object" ? payload.user : null,
    raw: payload,
  };
}

export async function fetchUserChatProfile({ token, userId } = {}) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) {
    throw new ApiError("userId is required", 400);
  }

  const payload = await apiRequest(`/api/users/${encodeURIComponent(normalizedUserId)}/chat-profile`, {
    method: "GET",
    headers: createAuthHeaders(token),
  });

  return normalizeChatProfile(normalizedUserId, payload);
}

/** Shared by the single and batch chat-profile readers so their parsing cannot drift. */
function normalizeChatProfile(userId, payload) {
  const displayName = typeof payload?.displayName === "string" ? payload.displayName.trim() : "";
  const encodedAvatar = typeof payload?.avatar === "string" ? payload.avatar.trim() : "";
  let avatar = "";
  if (encodedAvatar) {
    try {
      avatar = decodeURIComponent(encodedAvatar);
    } catch {
      avatar = encodedAvatar;
    }
    // A stored `data:` photo comes back as the API-RELATIVE avatar endpoint path instead of the
    // inlined bytes, and the renderer is never same-origin with the API — an unresolved path in
    // an <img> loads nothing, so every sender/member silently degrades to initials.
    avatar = resolveApiUrl(avatar);
  }
  return { userId, displayName, avatar, raw: payload };
}

/**
 * Resolve many chat profiles in ONE request, keyed by the ids you passed in.
 *
 * The per-user GET is a distinct url per id, so opening a busy thread issued ~30 of them AND ~30
 * CORS preflights (the preflight cache is per-url, so it could never help), which then queued
 * against the browser's connection limit. This is a POST to a constant url: one request, and one
 * preflight that caches.
 *
 * Ids the server cannot resolve are simply absent from the returned map.
 */
export async function fetchUserChatProfiles({ token, userIds } = {}) {
  const ids = Array.isArray(userIds)
    ? [...new Set(userIds.map((id) => String(id || "").trim()).filter(Boolean))]
    : [];
  if (ids.length === 0) {
    return {};
  }

  const payload = await apiRequest("/api/users/chat-profiles:resolve", {
    method: "POST",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ userIds: ids }),
  });

  const profiles = payload?.profiles && typeof payload.profiles === "object" ? payload.profiles : {};
  const out = {};
  for (const id of ids) {
    if (profiles[id]) {
      out[id] = normalizeChatProfile(id, profiles[id]);
    }
  }
  return out;
}

export async function fetchDoctors({ token, q } = {}) {
  const query = new URLSearchParams();
  const normalizedQuery = String(q || "").trim();
  if (normalizedQuery) {
    query.set("q", normalizedQuery);
  }

  const path = query.toString() ? `/api/users/doctors?${query.toString()}` : "/api/users/doctors";
  const payload = await apiRequest(path, {
    method: "GET",
    headers: createAuthHeaders(token),
  });

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    count: Number(payload?.count) || 0,
    raw: payload,
  };
}

/**
 * List the staff users the current user can start an internal chat with.
 * GET /api/users/internal-chat-partners
 *
 * Server-filtered to the configured eligible userTypes and excludes the caller. The optional
 * `q` is applied client-side (the endpoint returns the full eligible directory).
 * `context: "chat"` additionally applies the per-access-group chat-directory visibility
 * facet (admins + opted-in groups only) — the new-chat picker passes it; the Tasks board
 * omits it and keeps the full directory for assignees.
 * `includeHidden: true` (admin-only; ignored for everyone else) returns every active
 * eligible partner plus doctors even when `context: "chat"` would hide them or doctor is
 * omitted from eligibleUserTypes — New chat "Show hidden".
 */
export async function fetchInternalChatPartners({ token, q, context, includeHidden } = {}) {
  const query = new URLSearchParams();
  const normalizedQuery = String(q || "").trim();
  if (normalizedQuery) {
    query.set("q", normalizedQuery);
  }
  const normalizedContext = String(context || "").trim();
  if (normalizedContext) {
    query.set("context", normalizedContext);
  }
  if (includeHidden) {
    query.set("includeHidden", "true");
  }

  const path = query.toString()
    ? `/api/users/internal-chat-partners?${query.toString()}`
    : "/api/users/internal-chat-partners";
  const payload = await apiRequest(path, {
    method: "GET",
    headers: createAuthHeaders(token),
  });

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    count: Number(payload?.count) || 0,
    raw: payload,
  };
}

/**
 * Set the caller's availability status (available|busy|meeting|away|dnd).
 * PUT /api/users/me/availability — the backend broadcasts the change to same-tenant
 * staff over WebSocket, so colleagues' presence dots recolor live.
 */
export async function updateMyAvailability({ token, availability } = {}) {
  const payload = await apiRequest("/api/users/me/availability", {
    method: "PUT",
    headers: { ...createAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ availability: String(availability || "").trim() }),
  });
  return { availability: String(payload?.availability || "").trim() };
}

export async function fetchDoctorDetails({ token, doctorId } = {}) {
  const normalizedDoctorId = String(doctorId || "").trim();
  if (!normalizedDoctorId) {
    throw new ApiError("doctorId is required", 400);
  }

  const payload = await apiRequest(`/api/users/doctors/${encodeURIComponent(normalizedDoctorId)}`, {
    method: "GET",
    headers: createAuthHeaders(token),
  });

  return {
    doctor: payload?.doctor ?? null,
    raw: payload,
  };
}

export async function searchPatients({ token, searchTerm } = {}) {
  const payload = await apiRequest(`/api/users/patients:search`, {
    method: "POST",
    headers: {
      ...createAuthHeaders(token),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ searchTerm: String(searchTerm || "").trim() }),
  });

  return {
    items: Array.isArray(payload?.items) ? payload.items : [],
    count: Number(payload?.count) || 0,
    raw: payload,
  };
}
